import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { Segment } from '@models';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap
} from 'rxjs';

type AiSummaryState =
  | { status: 'loading' }
  | { status: 'success'; data: string | Segment[] }
  | { status: 'error' };

@Component({
  selector: 'ai-summary',
  templateUrl: './ai-summary.component.html',
  styleUrl: './ai-summary.component.scss'
})
export class AiSummaryComponent {
  @Input() summaryTitle = 'Study summary';
  @Input() summaryMaxWidth = '';
  @Input() collapsedHeight = '';
  @Input() expandable = false;
  @Input() set summary(value: Observable<string | Segment[]>) {
    this.summarySource$.next(value);
  }

  @Output() termClicked = new EventEmitter<Record<string, any>>();

  private summarySource$ = new BehaviorSubject<Observable<string | Segment[]>>(of('Loading...'));
  private retryTrigger$ = new Subject<void>();
  isExpanded = false;

  summaryState$: Observable<AiSummaryState> = this.summarySource$.pipe(
    switchMap((summary$) =>
      this.retryTrigger$.pipe(
        startWith(void 0),
        switchMap(() =>
          summary$.pipe(
            map((data) => ({ status: 'success', data }) as const),
            startWith({ status: 'loading' } as const),
            catchError(() => of({ status: 'error' } as const))
          )
        )
      )
    )
  );

  onSegmentClicked(segment: Segment): void {
    if (segment.metadata) {
      this.termClicked.emit(segment.metadata);
    }
  }

  getSegments(summary: string | Segment[]): Segment[] | undefined {
    return typeof summary === 'string' ? undefined : summary;
  }

  getSummaryText(summary: string | Segment[]): string | undefined {
    return typeof summary === 'string' ? summary : undefined;
  }

  getStateData(state: AiSummaryState): string | Segment[] | undefined {
    return state.status === 'success' ? state.data : undefined;
  }

  get isCollapsed(): boolean {
    return this.expandable && !this.isExpanded;
  }

  @HostBinding('style.maxWidth')
  get hostWidth(): string {
    return this.summaryMaxWidth;
  }

  @HostBinding('style.height')
  get hostHeight(): string | null {
    if (!this.expandable || !this.collapsedHeight) {
      return null;
    }

    return this.isExpanded ? 'auto' : this.collapsedHeight;
  }

  @HostBinding('style.minHeight')
  get hostMinHeight(): string | null {
    if (!this.expandable || !this.collapsedHeight) {
      return null;
    }
    return this.collapsedHeight;
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  onTryAgain(): void {
    this.retryTrigger$.next();
  }
}
